import { Injectable } from '@angular/core';
import { distinctUntilChanged, pairwise, Subject } from 'rxjs';
import { fabric } from 'fabric';
import { UntilDestroy } from '@ngneat/until-destroy';
import { map } from 'rxjs/operators';
import { SpaceBoardsService } from '../../../../services/space-boards.service';
import { FabricPdfPage } from '../../items-canvas/board-items/board-pdf';
import { PdfManagerService } from './pdf-manager.service';
// Values here are determined based on how the PDFs with the old rendering way used to look
export const PDF_PAGE_TOP_PADDING = 28;
export const PDF_PAGE_LEFT_PADDING = 22;

export const RENDERED_PDF_PAGES_SCALE = 0.95;

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class PdfPageToFabricService {
  private pdfPageShouldRenderMap = new Map<string, Subject<number>>();
  private pdfPageRenderCompletedMap = new Map<string, Subject<number>>();
  private pdfEnteredViewportMap = new Map<string, Subject<boolean>>();

  constructor(
    private pdfManagerService: PdfManagerService,
    private spaceBoardsService: SpaceBoardsService,
  ) {
    this.spaceBoardsService.activeSpaceSelectedBoard$
      .pipe(
        map((selectedBoard) => selectedBoard?.uid),
        distinctUntilChanged(),
        pairwise(),
      )
      .subscribe(([previousBoardUid]) => {
        if (previousBoardUid) {
          // only clearing the service if the user was on a board and moved to another board or out of the space
          this.clearService();
        }
      });
  }

  private clearService() {
    this.pdfPageShouldRenderMap.clear();
    this.pdfPageRenderCompletedMap.clear();
    this.pdfEnteredViewportMap.clear();
  }

  public getPdfPageShouldRenderSubject$(uid: string): Subject<number> {
    if (!this.pdfPageShouldRenderMap.has(uid)) {
      this.pdfPageShouldRenderMap.set(uid, new Subject<number>());
    }
    return this.pdfPageShouldRenderMap.get(uid)!;
  }

  public getPdfPageRenderCompletedSubject$(uid: string): Subject<number> {
    if (!this.pdfPageRenderCompletedMap.has(uid)) {
      this.pdfPageRenderCompletedMap.set(uid, new Subject<number>());
    }
    return this.pdfPageRenderCompletedMap.get(uid)!;
  }

  public getPdfEnteredViewportSubject$(uid: string): Subject<boolean> {
    if (!this.pdfEnteredViewportMap.has(uid)) {
      this.pdfEnteredViewportMap.set(uid, new Subject<boolean>());
    }
    return this.pdfEnteredViewportMap.get(uid)!;
  }

  public emitPdfPageShouldRender(contentId: string, pageIndex: number): void {
    const subject = this.getPdfPageShouldRenderSubject$(contentId);
    subject.next(pageIndex);
  }

  public emitPdfPageRenderCompleted(uid: string, pageIndex: number): void {
    const subject = this.getPdfPageRenderCompletedSubject$(uid);
    subject.next(pageIndex);
  }

  public emitPdfEnteredViewport(uid: string): void {
    const subject = this.getPdfEnteredViewportSubject$(uid);
    subject.next(true);
  }

  async getPdfPageAsFabricImage(contentId: string, url: string, pageIndex: number) {
    const pageWithViewport = await this.pdfManagerService.getPageWithViewport(
      contentId,
      url,
      pageIndex,
    );
    if (!pageWithViewport) {
      throw new Error('Could not get the page with viewport.');
    }

    const { pageProxy, shiftFromTop, viewport, height, width } = pageWithViewport;

    const canvas = document.createElement('canvas');
    canvas.height = viewport.height;
    canvas.width = viewport.width;
    const canvasContext = canvas.getContext('2d', { alpha: false });
    if (!canvasContext) {
      throw new Error('Could not create canvas context.');
    }

    await pageProxy.render({
      canvasContext: canvasContext,
      viewport,
    }).promise;

    // no other renders will override this one
    const imageUrl = await createImageBitmap(canvas);

    canvasContext.clearRect(0, 0, canvas.width, canvas.height);
    canvas.width = 0;
    canvas.height = 0;

    // Create a canvas to draw the bitmap, because fabric does not accept plain ImageBitmap
    const bitmapCanvas = document.createElement('canvas');
    bitmapCanvas.height = viewport.height;
    bitmapCanvas.width = viewport.width;

    const ctx = bitmapCanvas.getContext('2d');
    if (!ctx) {
      throw new Error('Could not create canvas context.');
    }

    ctx.drawImage(imageUrl, 0, 0);

    // Now use the bitmapCanvas as a source for fabric.Image
    const pageAsFabricImage: FabricPdfPage = new fabric.Image(bitmapCanvas);

    pageAsFabricImage.selectable = false;
    pageAsFabricImage.evented = false;
    pageAsFabricImage.hasControls = false;
    pageAsFabricImage.hasBorders = false;
    pageAsFabricImage.hasRotatingPoint = false;
    pageAsFabricImage.hasControls = false;
    pageAsFabricImage.hasBorders = false;
    pageAsFabricImage.hasRotatingPoint = false;
    pageAsFabricImage.lockScalingX = true;
    pageAsFabricImage.lockScalingY = true;
    pageAsFabricImage.lockRotation = true;
    pageAsFabricImage.lockUniScaling = true;

    // Because fabric does not respect the canvas size when rendering the image based on it
    pageAsFabricImage.scaleToHeight(height);
    pageAsFabricImage.scaleToWidth(width);

    // RENDERED_PDF_PAGES_SCALE is a scale we were setting before on the CSS level when the PDF was rendered on HTML canvases, we are keeping it for consistency.
    pageAsFabricImage.set({
      scaleX: (pageAsFabricImage.scaleX ?? 1) * RENDERED_PDF_PAGES_SCALE,
      scaleY: (pageAsFabricImage.scaleY ?? 1) * RENDERED_PDF_PAGES_SCALE,
    });

    pageAsFabricImage.shiftFromTop = shiftFromTop;

    pageProxy.cleanup(true);

    return pageAsFabricImage;
  }
}
